import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { fetchWrapper } from '../helpers';
import { API_ENDPOINT } from '../constants';
import { ProviderClinicalStatus } from '../admin/constants/general';

import type {
  GetProviderByIdPayload,
  GetProvidersByRolePayload,
  IGetRelationshipManagersArg,
  IProvider,
  IGuardianRelationshipManagerAssignment,
  IProviderLicensesMapping,
  IProviderLicenseDetails,
  IProviderPayorCredentialsMapping,
  IProviderPayorCredentialDetails,
  UpdateProviderByIdPayload,
  IRelationshipManagerAssignment,
} from '../utils/types';
import type { ActionReducerMapBuilder, PayloadAction, SerializedError } from '@reduxjs/toolkit';

const name = 'providers';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<ProviderState>) => createExtraReducers(builder),
});

export const buildGetProvidersUrl = (
  clinical_status: ProviderClinicalStatus | Array<ProviderClinicalStatus>,
  baseUrl: string,
) =>
  Array.isArray(clinical_status)
    ? clinical_status.reduce((url, status) => {
        return `${url}${`&clinical_status=${status}`}`;
      }, `${baseUrl}`)
    : `${baseUrl}/?clinical_status=${clinical_status}`;

export interface ProviderState {
  list: Array<IProvider>;
  listSource: 'clinical' | null;
  providersByRole: Array<IProvider>;
  allProvidersList: Array<IProvider>;
  provider: IProvider | null;
  providerLicensesMapping: IProviderLicensesMapping;
  providerPayorCredentialsMapping: IProviderPayorCredentialsMapping;
  providerLicensesErrors: Record<string, string>;
  providerPayorCredentialsErrors: Record<string, string>;
  lastFetchedProvider: IProvider | null;
  error: any;
  loading: boolean;
  createdProvider: IProvider | null;
  coassignedHomeBcbas: Array<IProvider>;
  coassignedBts: Array<IProvider>;
  coAssignedProviders: Array<IProvider>;
  relationshipManagerCandidates: Array<IProvider>;
  relationshipManagerAssignments: Array<IGuardianRelationshipManagerAssignment>;
  providerLicensesLoading: boolean;
  providerPayorCredentialsLoading: boolean;
  relationshipManagers: Array<IRelationshipManagerAssignment>;
  relationshipManagersLoading: boolean;
}

function createInitialState(): ProviderState {
  return {
    list: [],
    listSource: null,
    providersByRole: [],
    allProvidersList: [],
    provider: null,
    providerLicensesMapping: {},
    providerPayorCredentialsMapping: {},
    providerLicensesErrors: {},
    providerPayorCredentialsErrors: {},
    lastFetchedProvider: null,
    loading: false,
    error: null,
    createdProvider: null,
    coassignedHomeBcbas: [],
    coassignedBts: [],
    coAssignedProviders: [],
    relationshipManagerCandidates: [],
    relationshipManagerAssignments: [],
    providerLicensesLoading: false,
    providerPayorCredentialsLoading: false,
    relationshipManagers: [],
    relationshipManagersLoading: false,
  };
}

function createReducers() {
  return {
    clearList,
    clearLastFetchedProvider,
    clearCoAssignedProviders,
    clearRelationshipManagerData,
    clearProviderLicensingAndCredentialDetails,
    updateProviderLists,
    updateState,
  };
  function clearCoAssignedProviders(state: ProviderState) {
    state.coAssignedProviders = [];
  }
  function clearRelationshipManagerData(state: ProviderState) {
    state.relationshipManagerCandidates = [];
    state.relationshipManagerAssignments = [];
  }
  function clearLastFetchedProvider(state: ProviderState) {
    state.lastFetchedProvider = null;
  }

  function clearProviderLicensingAndCredentialDetails(state: ProviderState) {
    state.providerLicensesMapping = {};
    state.providerPayorCredentialsMapping = {};
    state.providerLicensesErrors = {};
    state.providerPayorCredentialsErrors = {};
    state.providerLicensesLoading = false;
    state.providerPayorCredentialsLoading = false;
  }

  function clearList(state: ProviderState) {
    state.list = [];
    state.listSource = null;
  }

  function updateProviderLists(state: ProviderState, action: PayloadAction<IProvider>) {
    const payload = action.payload;
    const { clinical_status } = payload;

    // Merge onto the existing entry rather than replace it: the invite action
    // returns only { id, clinical_status } (no PII), so a replace would blank the
    // provider's other fields in the lists. A full payload (promote/clinical
    // status) still overwrites every field, so this is equivalent for them.
    state.allProvidersList = state.allProvidersList.map((provider) =>
      provider.id === payload.id ? { ...provider, ...payload } : provider,
    );

    state.providersByRole = state.providersByRole.map((provider) =>
      provider.id === payload.id ? { ...provider, ...payload } : provider,
    );

    let nextList = state.list;
    if (state.listSource === 'clinical') {
      const providerInClinicalList = state.list.some((provider) => provider.id === payload.id);

      if (clinical_status === ProviderClinicalStatus.CLINICAL && !providerInClinicalList) {
        nextList = [...state.list, payload];
      } else if (clinical_status !== ProviderClinicalStatus.CLINICAL && providerInClinicalList) {
        nextList = state.list.filter((provider) => provider.id !== payload.id);
      }
    }

    state.list = nextList.map((provider) =>
      provider.id === payload.id ? { ...provider, ...payload } : provider,
    );

    if (state.lastFetchedProvider?.id === payload.id) {
      state.lastFetchedProvider = {
        ...state.lastFetchedProvider,
        ...payload,
      };
    }
  }

  function updateState(state: ProviderState, action: any) {
    const { key, value } = action.payload;
    Object.assign(state, { [key]: value });
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/providers`;

  return {
    getList: getList(),
    getProvidersByRole: getProvidersByRole(),
    updateProviderClinicalStatus: updateProviderClinicalStatus(),
    getProvider: getProvider(),
    getProviderById: getProviderById(),
    getRelationshipManagerCandidates: getRelationshipManagerCandidates(),
    getRelationshipManagerAssignments: getRelationshipManagerAssignments(),
    createProvider: createProvider(),
    updateProviderById: updateProviderById(),
    resetProviderPassword: resetProviderPassword(),
    getCoAssignedProviders: getCoAssignedProviders(),
    getProviderLicensingDetails: getProviderLicensingDetails(),
    getProviderPayorCredentials: getProviderPayorCredentials(),
    createProviderAssessmentSurvey: createProviderAssessmentSurvey(),
    promoteProvider: promoteProvider(),
    inviteProviderToPlatform: inviteProviderToPlatform(),
    updateQaBillingStatus: updateQaBillingStatus(),
    getRelationshipManagers: getRelationshipManagers(),
  };

  function getList() {
    return createAsyncThunk(
      `${name}/getList`,
      async ({ include_roles = false, clinical_status, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: clinical_status
            ? buildGetProvidersUrl(clinical_status, `${baseUrl}/?include_roles=${include_roles}`)
            : `${baseUrl}/?include_roles=${include_roles}`,
          getAccessTokenSilently,
        });
        if (!clinical_status || clinical_status === ProviderClinicalStatus.CLINICAL) {
          return { response, type: ProviderClinicalStatus.CLINICAL };
        } else {
          return { response };
        }
      },
    );
  }

  function getProvidersByRole() {
    return createAsyncThunk<IProvider[], GetProvidersByRolePayload>(
      `${name}/getProvidersByRole`,
      async ({ role, getAccessTokenSilently }) => {
        const response: IProvider[] = await fetchWrapper.get({
          url: `${baseUrl}/?include_roles=true&role=${role}`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getRelationshipManagerCandidates() {
    return createAsyncThunk<IProvider[], GetProvidersByRolePayload>(
      `${name}/getRelationshipManagerCandidates`,
      async ({ role, getAccessTokenSilently }) => {
        const response: IProvider[] = await fetchWrapper.get({
          url: `${baseUrl}/?include_roles=true&role=${role}`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getRelationshipManagerAssignments() {
    return createAsyncThunk<IGuardianRelationshipManagerAssignment[], GetProviderByIdPayload>(
      `${name}/getRelationshipManagerAssignments`,
      async ({ providerId, getAccessTokenSilently }) => {
        const response: IGuardianRelationshipManagerAssignment[] = await fetchWrapper.get({
          url: `${baseUrl}/${providerId}/relationship_manager/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function updateProviderClinicalStatus() {
    return createAsyncThunk(
      `${name}/updateProviderClinicalStatus`,
      async ({ data, getAccessTokenSilently, providerId }: any) => {
        const response = await fetchWrapper.put(
          { url: `${baseUrl}/${providerId}/clinical_status/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function getProvider() {
    return createAsyncThunk(`${name}/getProvider`, async ({ getAccessTokenSilently }: any) => {
      const response = await fetchWrapper.get({ url: `${baseUrl}/info`, getAccessTokenSilently });
      return response;
    });
  }

  function getProviderById() {
    return createAsyncThunk<IProvider, GetProviderByIdPayload>(
      `${name}/getProviderById`,
      async ({ getAccessTokenSilently, providerId }) => {
        const response: IProvider = await fetchWrapper.get({
          url: `${baseUrl}/${providerId}/?include_roles=true`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createProvider() {
    return createAsyncThunk(
      `${name}/createProvider`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function updateProviderById() {
    return createAsyncThunk<IProvider, UpdateProviderByIdPayload>(
      `${name}/updateProviderById`,
      async ({ providerId, data, getAccessTokenSilently }) => {
        const response: IProvider = await fetchWrapper.patch(
          { url: `${baseUrl}/${providerId}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function resetProviderPassword() {
    return createAsyncThunk(
      `${name}/resetProviderPassword`,
      async ({ providerId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post({
          url: `${baseUrl}/${providerId}/password_reset/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getProviderLicensingDetails() {
    return createAsyncThunk(
      `${name}/getProviderLicensingDetails`,
      async ({
        providerId,
        getAccessTokenSilently,
        state,
      }: {
        providerId: number | string;
        getAccessTokenSilently: () => Promise<string>;
        state: string;
      }) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${providerId}/provider_licenses/${state}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getProviderPayorCredentials() {
    return createAsyncThunk(
      `${name}/getProviderPayorCredentials`,
      async ({
        providerId,
        getAccessTokenSilently,
        payorId,
      }: {
        providerId: number | string;
        getAccessTokenSilently: () => Promise<string>;
        payorId: number | string;
      }) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${providerId}/provider_payor_credentials/${payorId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getCoAssignedProviders() {
    return createAsyncThunk(
      `${name}/getCoAssignedProviders`,
      async ({ getAccessTokenSilently, clinical_relationship, client_id, isActive }: any = {}) => {
        let url = `${baseUrl}/co_assigned/?`;

        if (Array.isArray(clinical_relationship)) {
          url += clinical_relationship
            .map((relationship: string) => `clinical_relationship=${relationship}`)
            .join('&');
        } else {
          url += `clinical_relationship=${clinical_relationship}`;
        }

        if (client_id) {
          url += `&client_id=${client_id}`;
        }

        const response = await fetchWrapper.get({
          url,
          getAccessTokenSilently,
        });
        return isActive
          ? response?.filter(
              (curProvider: IProvider) =>
                curProvider?.clinical_status === ProviderClinicalStatus.CLINICAL,
            )
          : response;
      },
    );
  }

  function createProviderAssessmentSurvey() {
    return createAsyncThunk(
      `${name}/createProviderAssessmentSurvey`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${API_ENDPOINT}/assessment_surveys/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function promoteProvider() {
    return createAsyncThunk(
      `${name}/promoteProvider`,
      async ({ providerId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.put(
          {
            url: `${baseUrl}/${providerId}/promote/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function inviteProviderToPlatform() {
    return createAsyncThunk(
      `${name}/inviteProviderToPlatform`,
      async ({ providerId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.put({
          url: `${baseUrl}/${providerId}/invite/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getRelationshipManagers() {
    return createAsyncThunk<IRelationshipManagerAssignment[], IGetRelationshipManagersArg>(
      `${name}/getRelationshipManagers`,
      async ({ guardianProviderId, getAccessTokenSilently }) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${guardianProviderId}/relationship_manager/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function updateQaBillingStatus() {
    return createAsyncThunk(
      `${name}/updateQaBillingStatus`,
      async ({ providerId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.put(
          {
            url: `${baseUrl}/${providerId}/qa_billing_status/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }
}

function mergeUpdatedProvider(current: IProvider | null, updated: IProvider): IProvider {
  return {
    ...current,
    ...updated,
    roles: updated.roles?.length ? updated.roles : current?.roles,
  };
}

function createExtraReducers(builder: ActionReducerMapBuilder<ProviderState>) {
  const {
    getProvider,
    getProviderById,
    getRelationshipManagerCandidates,
    getRelationshipManagerAssignments,
    updateProviderById,
    getList,
    getProvidersByRole,
    createProvider,
    resetProviderPassword,
    getCoAssignedProviders,
    createProviderAssessmentSurvey,
    updateProviderClinicalStatus,
    promoteProvider,
    inviteProviderToPlatform,
    updateQaBillingStatus,
    getProviderLicensingDetails,
    getProviderPayorCredentials,
    getRelationshipManagers,
  } = extraActions;

  builder
    .addCase(getProvider.pending, (state: ProviderState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getProvider.fulfilled, (state: ProviderState, action: PayloadAction<any>) => {
      state.provider = action.payload;
      state.loading = false;
    })
    .addCase(getProvider.rejected, (state: ProviderState, action: any) => {
      state.provider = null;
      state.error = action.error;
      state.loading = false;
    })
    .addCase(getProviderById.pending, (state: ProviderState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getProviderById.fulfilled,
      (state: ProviderState, action: PayloadAction<IProvider>) => {
        state.lastFetchedProvider = action.payload;
        state.loading = false;
      },
    )
    .addCase(getProviderById.rejected, (state, action) => {
      if (action.meta.aborted) return;
      state.lastFetchedProvider = null;
      toast.error(action.error.message, { toastId: 'getProviderByIdError' });
      state.loading = false;
      state.error = action.error;
    })
    .addCase(
      getRelationshipManagerCandidates.fulfilled,
      (state: ProviderState, action: PayloadAction<IProvider[]>) => {
        state.relationshipManagerCandidates = action.payload;
      },
    )
    .addCase(getRelationshipManagerCandidates.rejected, (state, action) => {
      if (action.meta.aborted) return;
      state.relationshipManagerCandidates = [];
      toast.error(action.error.message ?? 'Error fetching relationship manager candidates', {
        toastId: 'getRelationshipManagerCandidatesError',
      });
    })
    .addCase(
      getRelationshipManagerAssignments.fulfilled,
      (state: ProviderState, action: PayloadAction<IGuardianRelationshipManagerAssignment[]>) => {
        state.relationshipManagerAssignments = action.payload;
      },
    )
    .addCase(getRelationshipManagerAssignments.rejected, (state, action) => {
      if (action.meta.aborted) return;
      state.relationshipManagerAssignments = [];
      toast.error(action.error.message ?? 'Error fetching relationship manager assignment', {
        toastId: 'getRelationshipManagerAssignmentsError',
      });
    })
    .addCase(updateProviderById.pending, (state: ProviderState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updateProviderById.fulfilled,
      (state: ProviderState, action: PayloadAction<IProvider>) => {
        state.lastFetchedProvider = mergeUpdatedProvider(
          state.lastFetchedProvider,
          action.payload,
        );
        state.allProvidersList = state.allProvidersList.map((provider: IProvider) =>
          provider.id === action.payload.id
            ? mergeUpdatedProvider(provider, action.payload)
            : provider,
        );
        state.list = state.list.map((provider: IProvider) =>
          provider.id === action.payload.id
            ? mergeUpdatedProvider(provider, action.payload)
            : provider,
        );
        state.providersByRole = state.providersByRole.map((provider: IProvider) =>
          provider.id === action.payload.id
            ? mergeUpdatedProvider(provider, action.payload)
            : provider,
        );

        state.loading = false;
      },
    )
    .addCase(updateProviderById.rejected, (state, action) => {
      state.error = action.error;
      state.loading = false;
    })
    .addCase(resetProviderPassword.pending, (state: ProviderState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(resetProviderPassword.fulfilled, (state: ProviderState) => {
      state.loading = false;
    })
    .addCase(resetProviderPassword.rejected, (state: ProviderState, action: any) => {
      state.error = action.error;
      state.loading = false;
    })
    .addCase(getList.pending, (state: ProviderState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getList.fulfilled, (state: ProviderState, action: any) => {
      // Temporary solution until BE endpoint is updated
      if (action.payload.type === ProviderClinicalStatus.CLINICAL) {
        state.list = action.payload.response;
        state.listSource = 'clinical';
      } else {
        state.allProvidersList = action.payload.response;
      }
      state.error = null;
      state.loading = false;
    })
    .addCase(getList.rejected, (state: ProviderState, action: any) => {
      state.list = [];
      state.listSource = null;
      state.error = action.error;
      state.loading = false;
    })
    //
    .addCase(getProvidersByRole.pending, (state: ProviderState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getProvidersByRole.fulfilled,
      (state: ProviderState, action: PayloadAction<IProvider[]>) => {
        state.providersByRole = action.payload;
        state.loading = false;
      },
    )
    .addCase(getProvidersByRole.rejected, (state: ProviderState, action: any) => {
      state.providersByRole = [];
      state.error = action.error;
      state.loading = false;
    })
    // For provider post
    .addCase(createProvider.pending, (state: ProviderState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createProvider.fulfilled, (state: ProviderState, action: PayloadAction<IProvider>) => {
      state.createdProvider = action.payload;
      state.loading = false;
      toast.success('New Provider Created', { toastId: 'createProviderSuccess' });
    })
    .addCase(createProvider.rejected, (state: ProviderState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message, { toastId: 'createProviderError' });
    })

    //get co assigned providers
    .addCase(getCoAssignedProviders.pending, (state: ProviderState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getCoAssignedProviders.fulfilled,
      (state: ProviderState, action: PayloadAction<any[]>) => {
        state.coAssignedProviders = action.payload;
        state.loading = false;
      },
    )
    .addCase(getCoAssignedProviders.rejected, (state: ProviderState, action: any) => {
      state.coAssignedProviders = [];
      state.error = action.error;
      state.loading = false;
    })

    // for creating provider assessment survey
    .addCase(createProviderAssessmentSurvey.pending, (state: ProviderState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createProviderAssessmentSurvey.fulfilled, (state: ProviderState) => {
      state.loading = false;
      toast.success('Assessment survey initiated');
    })
    .addCase(createProviderAssessmentSurvey.rejected, (state: ProviderState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // updateProviderClinicalStatus()
    .addCase(updateProviderClinicalStatus.pending, (state: ProviderState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateProviderClinicalStatus.fulfilled, (state: ProviderState) => {
      state.loading = false;
      toast.success('Clinical status updated');
    })
    .addCase(updateProviderClinicalStatus.rejected, (state: ProviderState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // for promoting provider
    .addCase(promoteProvider.pending, (state: ProviderState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(promoteProvider.fulfilled, (state: ProviderState) => {
      state.loading = false;
      toast.success('Provider promoted');
    })
    .addCase(promoteProvider.rejected, (state: ProviderState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // for inviting a pre-clinical parent to the platform
    .addCase(inviteProviderToPlatform.pending, (state: ProviderState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(inviteProviderToPlatform.fulfilled, (state: ProviderState) => {
      state.loading = false;
      toast.success('Invitation sent');
    })
    .addCase(inviteProviderToPlatform.rejected, (state: ProviderState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // for updating qa billing status
    .addCase(updateQaBillingStatus.pending, (state: ProviderState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateQaBillingStatus.fulfilled, (state: ProviderState) => {
      state.loading = false;
      toast.success('QA billing status updated successfully');
    })
    .addCase(updateQaBillingStatus.rejected, (state: ProviderState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // getProviderLicensingDetails()
    .addCase(getProviderLicensingDetails.pending, (state: ProviderState) => {
      state.providerLicensesLoading = true;
      state.error = null;
    })
    .addCase(
      getProviderLicensingDetails.fulfilled,
      (
        state: ProviderState,
        action: PayloadAction<IProviderLicenseDetails> & {
          meta: { arg: { providerId: number | string; state: string } };
        },
      ) => {
        const key = `${action.meta.arg.providerId}-${action.meta.arg.state}`;
        delete state.providerLicensesErrors[key];
        state.providerLicensesMapping[key] = action.payload;
        state.providerLicensesLoading = false;
      },
    )
    .addCase(
      getProviderLicensingDetails.rejected,
      (
        state: ProviderState,
        action: {
          error: SerializedError;
          meta: { arg: { providerId: number | string; state: string } };
        },
      ) => {
        const key = `${action.meta.arg.providerId}-${action.meta.arg.state}`;
        const message = action.error?.message ?? 'Error fetching provider licensing details';
        state.providerLicensesErrors[key] = message;
        state.providerLicensesLoading = false;
        toast.error(message);
      },
    )
    // getProviderPayorCredentials()
    .addCase(getProviderPayorCredentials.pending, (state: ProviderState) => {
      state.providerPayorCredentialsLoading = true;
      state.error = null;
    })
    .addCase(
      getProviderPayorCredentials.fulfilled,
      (
        state: ProviderState,
        action: PayloadAction<IProviderPayorCredentialDetails> & {
          meta: { arg: { providerId: number | string; payorId: number | string } };
        },
      ) => {
        const key = `${action.meta.arg.providerId}-${action.meta.arg.payorId}`;
        delete state.providerPayorCredentialsErrors[key];
        state.providerPayorCredentialsMapping[key] = action.payload;
        state.providerPayorCredentialsLoading = false;
      },
    )
    .addCase(
      getProviderPayorCredentials.rejected,
      (
        state: ProviderState,
        action: {
          error: SerializedError;
          meta: { arg: { providerId: number | string; payorId: number | string } };
        },
      ) => {
        const key = `${action.meta.arg.providerId}-${action.meta.arg.payorId}`;
        const message = action.error?.message ?? 'Error fetching provider payor credentials';
        state.providerPayorCredentialsErrors[key] = message;
        state.providerPayorCredentialsLoading = false;
        toast.error(message);
      },
    )
    // getRelationshipManagers()
    .addCase(getRelationshipManagers.pending, (state: ProviderState) => {
      state.relationshipManagersLoading = true;
      state.error = null;
    })
    .addCase(
      getRelationshipManagers.fulfilled,
      (state: ProviderState, action: PayloadAction<IRelationshipManagerAssignment[]>) => {
        state.relationshipManagers = action.payload;
        state.relationshipManagersLoading = false;
      },
    )
    .addCase(getRelationshipManagers.rejected, (state: ProviderState, action) => {
      state.relationshipManagers = [];
      state.error = action.error;
      state.relationshipManagersLoading = false;
    });
}

export const providerActions = { ...slice.actions, ...extraActions };
export const providerReducer = slice.reducer;
